<script>
// Registry-driven nav. Adding a new app = one entry in src/apps/registry.js.
import { APPS, HOME_APP, NAV_APPS, APP_BY_ID } from '../apps/registry'
import {
  getBeaconNetwork,
  TXL_CONTRACT_ADDRESS,
  SQUARES_CONTRACT_ADDRESS,
  INVITE_ALLOWLIST,
  BLOCKCHAIN_ENABLED,
} from '../constants'
import { getContractStorage, getBigmapKey, tzktGet } from '../services/tzkt'

const BALANCES_VISIBLE_KEY = 'tezliteapps:hamburgerBalancesVisible'
const BETA_UNLOCK_KEY = 'tezliteapps:betaUnlock'

// Beta-tier unlock. Once a visitor lands on the site with `?beta=1` in
// the URL, persist the choice in localStorage so they don't have to
// re-paste the param on every visit. The admin wallet always sees beta
// + private tiers regardless of this flag.
function loadBetaUnlock() {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const params = new URLSearchParams(window.location.search)
      if (params.get('beta') === '1') {
        window.localStorage.setItem(BETA_UNLOCK_KEY, '1')
        return true
      }
      return window.localStorage.getItem(BETA_UNLOCK_KEY) === '1'
    }
  } catch (_e) { /* unavailable — default to locked */ }
  return false
}

// Hamburger balances start hidden — the user must explicitly opt in.
// Persist the choice via localStorage so it survives reloads. Wrapped in
// try/catch because some browsers (Safari private mode, SSR) throw on
// localStorage access; in those cases we fall back to the default.
function loadBalancesVisible() {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      return window.localStorage.getItem(BALANCES_VISIBLE_KEY) === '1'
    }
  } catch (_e) { /* unavailable — default to hidden */ }
  return false
}

function saveBalancesVisible(v) {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(BALANCES_VISIBLE_KEY, v ? '1' : '0')
    }
  } catch (_e) { /* ignore */ }
}

export default {
  name: 'mainBody',
  // We register every app's component up-front so <component :is> can resolve.
  components: Object.fromEntries(APPS.map((a) => [a.id, a.component])),
  props: ['wallet', 'socket', 'tezos'],
  data() {
    return {
      APPS,
      NAV_APPS,
      HOME_APP,
      activeView: HOME_APP.id,
      walletAddress: 'SYNC WALLET',
      // TXL manager contract state, polled from storage so the holder pool
      // total + the connected wallet's claimable share show on every page.
      txlPoolValue: 0,
      txlShare: 0,
      txlOwnsNft: false,
      // TXL token IDs the connected wallet owns. Populated by
      // refreshTxlContract from the idLookUp big_map; consumed by
      // payNftHolderBC to populate the claim(tokenIds) call. v1 didn't
      // need this — the old payTxlHolder swept per-NFT balances the
      // contract attributed to the sender, no input.
      txlOwnedTokenIds: [],
      txlCashStatus: '',
      // Flashes the "Unclaimed" chip green for ~1s when the pool grows (a bet
      // somewhere sent its holder fee in). txlPoolSeeded guards the first poll
      // so the initial 0 → real-value jump doesn't trigger a flash.
      txlPoolFlash: false,
      txlPoolSeeded: false,
      // Mobile-only hamburger dropdown anchored to the wallet button.
      // Holds "Sign in / Sign out" and "Cash out" actions so the topBanner
      // doesn't have to fit two long pills on a 360px screen.
      walletMenuOpen: false,
      // Wallet balances + on-chain claimable totals shown in the hamburger.
      // Hidden by default — the user must opt-in via the "Show balances"
      // menu item, which flips the localStorage flag below. Display rows
      // render as masked dots (• • •) when hidden so the layout doesn't
      // jump when a user toggles visibility.
      myAddress: '',
      walletBalanceTez: null,           // null = not yet fetched
      claimableSquaresTez: null,        // pending big_map[myAddress] in tez
      balancesVisible: loadBalancesVisible(),
      // Tier gating — see registry.js. Beta unlock comes from URL/localStorage;
      // admin unlock comes from a wallet check (only the deployer sees private
      // tiles regardless of any URL param).
      betaUnlocked: loadBetaUnlock(),
    }
  },
  computed: {
    activeComponent() {
      return APP_BY_ID[this.activeView]?.component || HOME_APP.component
    },
    // The carousel order: TXL Manager (home) first, then every nav app.
    // Apps whose mainnet KT1 is still a placeholder get pushed to the
    // right end so "ready to play" pills sit on the left of the nav
    // strip. Two filters preserves the registry's relative order within
    // each group (Array.prototype.sort stability isn't guaranteed across
    // engines).
    // True when the connected wallet is on the operator's invite allowlist
    // (constants.js INVITE_ALLOWLIST). Unlocks every tier including
    // 'private' in PLAY mode. Falsy when no wallet is connected.
    isInvited() {
      return !!this.myAddress && INVITE_ALLOWLIST.includes(this.myAddress)
    },
    // True when the connected wallet holds at least one TXL NFT (per the
    // already-running refreshTxlContract poll). TXL holders are project
    // stakeholders — they get to PREVIEW the gated apps so they can see
    // what they're invested in, but every game component refuses
    // transactions in preview mode.
    isTxlHolder() {
      return !!this.txlOwnsNft
    },
    // PREVIEW vs PLAY. Invited operators play normally; TXL holders who
    // aren't on the invite list see gated apps in read-only/preview mode
    // (wager + buy methods early-return with a status message). Default
    // visitors and ?beta=1 testers don't enter preview — beta gating
    // for them still lets them play.
    previewMode() {
      return this.isTxlHolder && !this.isInvited
    },
    // Visibility rules applied to a single registry entry. Tier comparisons:
    //   public:   always visible
    //   beta:     visible when betaUnlocked, invited, or TXL-holder preview
    //   private:  visible when invited OR TXL-holder preview (?beta=1
    //             alone does NOT unlock private — the URL flag is for
    //             public testers, who don't see private regardless)
    // Entries without an explicit tier default to public.
    tierVisible() {
      return (entry) => {
        const tier = entry.tier || 'public'
        if (tier === 'public') return true
        if (tier === 'beta') return this.betaUnlocked || this.isInvited || this.isTxlHolder
        if (tier === 'private') return this.isInvited || this.isTxlHolder
        return true
      }
    },
    navTiles() {
      const all = [this.HOME_APP, ...this.NAV_APPS].filter(this.tierVisible)
      return [
        ...all.filter((a) => a.mainnetReady),
        ...all.filter((a) => !a.mainnetReady),
      ]
    },
    // True when any non-public tile is currently in the nav. Drives the
    // "Private development build" banner — only shown when something
    // gated is actually being exposed, so default visitors never see it.
    hasGatedTilesVisible() {
      return this.navTiles.some((t) => (t.tier || 'public') !== 'public')
    },
    // Format helpers for the hamburger balance rows. Render "• • •"
    // when the user has chosen to hide balances, the actual number
    // (3-decimal tez) when visible, or "…" while the first fetch is
    // still in flight. Both rows share the same shape so the layout
    // doesn't shift when only one of the two resolves.
    walletBalanceDisplay() {
      if (!this.balancesVisible) return '• • •'
      if (this.walletBalanceTez == null) return '…'
      return `${this.walletBalanceTez.toFixed(3)} tez`
    },
    // Short-form address for the menu's "Address" row. Shows the first
    // 6 + last 4 of the tz1/tz2 (which side of the wallet is rendered
    // doesn't matter; the goal is "yes, you're connected as this") OR
    // an em-dash when no wallet is paired.
    addressDisplay() {
      if (!this.myAddress) return '—'
      return `${this.myAddress.slice(0, 6)}…${this.myAddress.slice(-4)}`
    },
    // Label for the access-tier row. Tells the user up front whether
    // their currently-connected wallet unlocks the gated apps or not —
    // surfaces the result of the INVITE_ALLOWLIST + TXL-holder checks
    // so users don't have to guess why squares isn't showing up.
    accessTierDisplay() {
      if (!this.myAddress) return 'Not connected'
      if (this.isInvited) return 'Operator (full)'
      if (this.isTxlHolder) return 'TXL preview'
      return 'Public only'
    },
    claimableSquaresDisplay() {
      if (!this.balancesVisible) return '• • •'
      if (this.claimableSquaresTez == null) return '…'
      return `${this.claimableSquaresTez.toFixed(3)} tez`
    },
  },
  created() {
    this.socket.on('newWallet', (newWallet) => {
      this.walletAddress = typeof newWallet === 'string' ? newWallet : 'SYNC WALLET'
      // Don't wait for the 30s polling tick — every wallet event should
      // immediately refresh the on-chain state both panels depend on.
      // Without this, a freshly connected Kukai address isn't recognized
      // as invited/TXL-holder until the next poll fires, so the gated
      // tiles (squares, etc.) stay hidden for up to 30 seconds.
      this.refreshHamburgerBalances()
      this.refreshTxlContract()
    })
    this.socket.on('selectGame', (game) => {
      this.selectGame(game)
    })
  },
  mounted() {
    // Poll the TXL manager contract for the holder pool total and — when a
    // wallet is connected — that wallet's claimable share. Lives here (not in
    // browseNFTs) so the Cash Out button + stats appear on every page.
    this.refreshTxlContract()
    this.refreshHamburgerBalances()
    if (BLOCKCHAIN_ENABLED) {
      this.txlPollInterval = setInterval(() => this.refreshTxlContract(), 30000)
      // Balances poll less aggressively — they're hamburger-only chrome,
      // not gameplay-critical.
      this.balancesPollInterval = setInterval(() => this.refreshHamburgerBalances(), 30000)
    }
  },
  beforeUnmount() {
    if (this.txlPollInterval) clearInterval(this.txlPollInterval)
    if (this.balancesPollInterval) clearInterval(this.balancesPollInterval)
    if (this.txlFlashTimeout) clearTimeout(this.txlFlashTimeout)
  },
  methods: {
    selectGame(id) {
      // Accept legacy 'welcome' alias.
      const target = id === 'welcome' ? HOME_APP.id : id
      if (!APP_BY_ID[target]) return
      this.activeView = target
      // Special hook: TezTacToe wants this every time it becomes active.
      if (target === 'tezTacToe') {
        this.socket.emit('updatePlayerControl')
      }
      this.$nextTick(() => this.scrollActiveIntoView())
    },
    // Scroll the carousel strip by ±~70% of its visible width per click.
    // Smooth-scroll is browser-native; no JS easing needed.
    scrollNav(dir) {
      const strip = this.$refs.navStrip
      if (!strip) return
      const delta = Math.max(120, Math.round(strip.clientWidth * 0.7))
      strip.scrollBy({ left: dir * delta, behavior: 'smooth' })
    },
    toggleWalletMenu() {
      this.walletMenuOpen = !this.walletMenuOpen
    },
    closeWalletMenu() {
      this.walletMenuOpen = false
    },
    async signInOrOutFromMenu() {
      this.walletMenuOpen = false
      await this.toggleWallet()
    },
    async cashOutFromMenu() {
      this.walletMenuOpen = false
      await this.payNftHolderBC()
    },
    // Permanently available in the menu so users can recover from a
    // dead Beacon pairing without having to wait for an error-state pill
    // to appear. resetWallet wipes the Beacon localStorage cache + reloads
    // the page; the next "Sign in" click triggers a fresh requestPermissions.
    resetWalletFromMenu() {
      this.walletMenuOpen = false
      this.resetWallet()
    },
    toggleBalancesVisible() {
      this.balancesVisible = !this.balancesVisible
      saveBalancesVisible(this.balancesVisible)
      if (this.balancesVisible) {
        // Refresh immediately so the user sees real numbers as soon as
        // they unhide, instead of waiting for the next 30s poll tick.
        this.refreshHamburgerBalances()
      }
    },
    // Pull the connected wallet's tez balance via RPC and its squares-
    // contract claimable balance via tzkt. Called from mounted() and on
    // a 30s interval. Silently no-ops when no wallet is connected (the
    // displays just stay at "…" then). Failures null the display so the
    // user sees the dash instead of a stale number.
    async refreshHamburgerBalances() {
      try {
        const account = await this.wallet?.client?.getActiveAccount?.()
        const address = account?.address || ''
        this.myAddress = address
        if (!address) {
          this.walletBalanceTez = null
          this.claimableSquaresTez = null
          return
        }
        // Wallet balance — Taquito's RPC client handles the math.
        try {
          const balMutez = await this.tezos?.tz?.getBalance?.(address)
          this.walletBalanceTez = balMutez != null ? Number(balMutez) / 1_000_000 : null
        } catch (_e) {
          this.walletBalanceTez = null
        }
        // Squares claimable — read the pending big_map id off storage,
        // then the wallet's row. tzkt returns null when the key doesn't
        // exist (no pending claim for this address) — treat as zero.
        try {
          const storage = await getContractStorage(SQUARES_CONTRACT_ADDRESS)
          const pendingBigmapId = storage?.pending
          if (pendingBigmapId == null) {
            this.claimableSquaresTez = 0
          } else {
            const value = await getBigmapKey(pendingBigmapId, address)
            this.claimableSquaresTez = value != null ? Number(value) / 1_000_000 : 0
          }
        } catch (_e) {
          this.claimableSquaresTez = null
        }
      } catch (e) {
        console.warn('refreshHamburgerBalances failed:', e?.message)
      }
    },
    // Keep the selected pill in view when nav changes via socket / keyboard.
    scrollActiveIntoView() {
      const strip = this.$refs.navStrip
      if (!strip) return
      const active = strip.querySelector('.navPill--active')
      if (!active) return
      const stripRect = strip.getBoundingClientRect()
      const pillRect = active.getBoundingClientRect()
      if (pillRect.left < stripRect.left || pillRect.right > stripRect.right) {
        active.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' })
      }
    },
    async toggleWallet() {
      // Currently connected? Disconnect.
      try {
        const activeAccount = await this.wallet.client.getActiveAccount()
        if (activeAccount) {
          await this.wallet.clearActiveAccount()
          this.walletAddress = 'SYNC WALLET'
          return
        }
      } catch (err) {
        console.warn('getActiveAccount failed (continuing to connect):', err?.message)
      }

      // Not connected — request permissions on the *current* network.
      // Passing `network` explicitly matters: without it Beacon falls back
      // to its built-in default (mainnet) which mismatches our CUSTOM
      // shadownet config and causes a slow re-handshake.
      const network = getBeaconNetwork()
      this.walletAddress = 'CONNECTING…'
      try {
        await this.wallet.client.requestPermissions({ network })
        this.tezos.setWalletProvider(this.wallet)
        // ACTIVE_ACCOUNT_SET event handler in App.vue will update the badge
      } catch (err) {
        // User-visible message. Most common failure modes: relay DNS issue
        // (we now mitigate via custom matrixNodes), user closed the popup,
        // wallet extension locked, or the wallet not supporting shadownet.
        const msg = err?.message || String(err)
        const name = err?.name || ''
        const isAborted = /aborted|cancel|denied/i.test(msg)
        const isNetworkUnsupported =
          name === 'NetworkNotSupportedBeaconError' ||
          /network.*not.*support|not.*support.*network|network_not_supported/i.test(msg)
        const isNetwork = /timeout|network|relay|resolve|ENOTFOUND|ERR_NAME/i.test(msg)
        if (isAborted) {
          this.walletAddress = 'SYNC WALLET (cancelled)'
        } else if (isNetworkUnsupported) {
          // Kukai's hosted web wallet doesn't implement shadownet. Temple
          // (extension) does — steer the user there.
          this.walletAddress = 'SYNC WALLET (use Temple for shadownet)'
          console.warn(
            'Wallet rejected shadownet. Kukai\'s web wallet does not support ' +
            'it — use Temple (browser extension), which connects to custom / ' +
            'test networks.',
            err,
          )
        } else if (isNetwork) {
          this.walletAddress = 'SYNC WALLET (relay error)'
          console.warn(
            'Beacon relay failed. If this persists, the Matrix relay list in ' +
            'constants.js may need refresh. See https://docs.walletbeacon.io',
            err,
          )
        } else {
          this.walletAddress = 'SYNC WALLET (failed)'
          console.warn('Wallet connect failed:', err)
        }
      }
    },
    /**
     * Nuke ALL Beacon storage and reload. Use this when Beacon's relay
     * connection is wedged — it forces a completely fresh handshake.
     * More aggressive than clearStaleBeaconStorage() (which only removes
     * known-bad-host entries).
     */
    resetWallet() {
      try {
        const ls = window.localStorage
        const toRemove = []
        for (let i = 0; i < ls.length; i++) {
          const k = ls.key(i)
          if (k && k.startsWith('beacon')) toRemove.push(k)
        }
        for (const k of toRemove) ls.removeItem(k)
        // Also clear the beacon IndexedDB if present
        if (window.indexedDB && window.indexedDB.deleteDatabase) {
          try { window.indexedDB.deleteDatabase('beacon') } catch (_e) { /* noop */ }
          try { window.indexedDB.deleteDatabase('beacon-sdk') } catch (_e) { /* noop */ }
        }
      } catch (e) {
        console.warn('resetWallet cleanup failed:', e)
      }
      window.location.reload()
    },
    // Poll the TXL v2 manager: the holder pool total (storage
    // totalRewards), and — if a wallet is connected — that wallet's
    // claimable share. v2 uses a global-accumulator pattern:
    //
    //   accrued(token) = accPerToken - idLookUp[token].lastSeenAcc
    //   claimable(addr) = sum(accrued(t)) over t owned by addr  +  pending[addr]
    //
    // We fetch the owned tokens by filtering the idLookUp big_map on
    // value.owner == myAddress (tzkt supports that natively), then add
    // any already-settled balance from the pending big_map.
    //
    // v1 used to walk an inline JS object on storage.idLookUp; v2 lifted
    // that to a big_map so storage returns just the integer pointer. The
    // old `for (const entry of Object.values(storage.idLookUp))` silently
    // iterated over zero entries against the new contract.
    async refreshTxlContract() {
      const storage = await getContractStorage(TXL_CONTRACT_ADDRESS)
      if (!storage) return
      const nextPool = Number(storage.totalRewards || 0) / 1e6
      if (this.txlPoolSeeded && nextPool > this.txlPoolValue) this.flashTxlPool()
      this.txlPoolValue = nextPool
      this.txlPoolSeeded = true

      const activeAccount = await this.wallet?.client?.getActiveAccount?.()
      const address = activeAccount?.address
      if (!address) {
        this.txlOwnsNft = false
        this.txlShare = 0
        this.txlOwnedTokenIds = []
        return
      }

      // v2 storage shape: totalRewards/accPerToken/dust are plain values;
      // idLookUp/pending are returned as bigmap pointer ids (integers).
      // If the storage shape ever shifts back to inlined maps, the Number()
      // coercion below would yield NaN and we'd safely fall through to
      // "no share" rather than throwing.
      const accPerToken = Number(storage.accPerToken || 0)
      const idLookUpId = Number(storage.idLookUp)
      const pendingId = Number(storage.pending)
      if (!Number.isFinite(idLookUpId)) {
        this.txlOwnsNft = false
        this.txlShare = 0
        this.txlOwnedTokenIds = []
        return
      }

      // tzkt: filter the idLookUp big_map for entries whose value.owner
      // matches us. `active=true` skips deleted entries. The 300 cap
      // covers the worst case of a single wallet holding every TXL
      // token (totalSupply = 271).
      const ownedEntries = await tzktGet(
        `/v1/bigmaps/${idLookUpId}/keys?active=true&value.owner=${address}&limit=300`,
      )
      let accruedMutez = 0
      const ownedTokenIds = []
      if (Array.isArray(ownedEntries)) {
        for (const e of ownedEntries) {
          const tokenId = Number(e.key)
          if (!Number.isFinite(tokenId)) continue
          const lastSeenAcc = Number(e.value?.lastSeenAcc || 0)
          ownedTokenIds.push(tokenId)
          const share = accPerToken - lastSeenAcc
          if (share > 0) accruedMutez += share
        }
      }

      // Already-settled balance for this address. settleBatch (admin) or
      // owner-change settlement (oracle) may have parked credit here
      // ahead of any direct claim.
      let pendingMutez = 0
      if (Number.isFinite(pendingId)) {
        const pendingKey = await getBigmapKey(pendingId, address)
        if (Array.isArray(pendingKey) && pendingKey[0]?.active) {
          pendingMutez = Number(pendingKey[0].value || 0)
        }
      }

      this.txlOwnedTokenIds = ownedTokenIds
      this.txlOwnsNft = ownedTokenIds.length > 0
      this.txlShare = (accruedMutez + pendingMutez) / 1e6
    },
    // Claim this wallet's accrued TXL holder earnings. v2's claim()
    // takes the list of token IDs the caller owns; the contract verifies
    // ownership, settles the accrued share for each, and sends the
    // caller their full pending balance. The contract caps the list at
    // MAX_BATCH (50) per call, so chunk if the wallet owns >50 NFTs.
    async payNftHolderBC() {
      const activeAccount = await this.wallet.client.getActiveAccount()
      if (!activeAccount) {
        this.txlCashStatus = 'Sync a wallet first'
        return
      }
      if (!this.txlOwnedTokenIds.length) {
        this.txlCashStatus = 'No TXL tokens owned'
        return
      }
      this.txlCashStatus = 'Cashing out…'
      this.tezos.setWalletProvider(this.wallet)
      try {
        const contract = await this.tezos.wallet.at(TXL_CONTRACT_ADDRESS)
        // claim's parameter type is bare `list nat` (SmartPy unwraps the
        // single-field record), so we use .methods.<name>(arg) — not
        // .methodsObject which expects a keyed record.
        const BATCH = 50
        for (let i = 0; i < this.txlOwnedTokenIds.length; i += BATCH) {
          const chunk = this.txlOwnedTokenIds.slice(i, i + BATCH)
          const op = await contract.methods.claim(chunk).send()
          await op.confirmation()
        }
        this.txlCashStatus = 'Cashed out!'
        this.refreshTxlContract()
      } catch (error) {
        console.error('payNftHolderBC failed:', error)
        this.txlCashStatus = 'Cash out failed'
      }
    },
    // Pulse the "Unclaimed" chip green for ~1s. Re-arm the timer each call so
    // back-to-back increases don't leave it stuck on.
    flashTxlPool() {
      this.txlPoolFlash = true
      if (this.txlFlashTimeout) clearTimeout(this.txlFlashTimeout)
      this.txlFlashTimeout = setTimeout(() => { this.txlPoolFlash = false }, 1000)
    },
  },
}
</script>

<template>
  <div class="mainBody">
    <div class="centerBody">
      <div class="gameManagement">
        <div class="rowFlex topBanner">
          <!-- Desktop wallet pill: full status text, direct toggle. Hidden
               at ≤480px via CSS, where the mobile hamburger replaces it. -->
          <div
            class="actionButton topBanner__wallet topBanner__wallet--desktop"
            @click="toggleWallet"
            :title="walletAddress"
          >{{ walletAddress }}</div>
          <!-- Mobile wallet pill: hamburger icon → dropdown with Sign in/out
               and Cash out. Hidden on desktop. -->
          <div class="topBanner__walletWrap">
            <div
              class="actionButton topBanner__wallet topBanner__wallet--mobile"
              @click="toggleWalletMenu"
              :title="walletAddress"
              :aria-expanded="walletMenuOpen ? 'true' : 'false'"
              aria-haspopup="menu"
              role="button"
              tabindex="0"
              @keydown.enter.prevent="toggleWalletMenu"
              @keydown.space.prevent="toggleWalletMenu"
              @keydown.esc.prevent="closeWalletMenu"
            >
              <span class="hamburger" :class="{ 'hamburger--open': walletMenuOpen }" aria-hidden="true">
                <span></span><span></span><span></span>
              </span>
            </div>
            <div
              v-if="walletMenuOpen"
              class="walletMenu"
              role="menu"
              @click.stop
            >
              <button
                type="button"
                class="walletMenu__item"
                role="menuitem"
                @click="signInOrOutFromMenu"
              >{{ walletAddress.startsWith('UNSYNC') ? 'Sign out' : 'Sign in' }}</button>
              <button
                type="button"
                class="walletMenu__item"
                role="menuitem"
                @click="cashOutFromMenu"
              >Cash out TXL</button>
              <button
                type="button"
                class="walletMenu__item walletMenu__item--reset"
                role="menuitem"
                @click="resetWalletFromMenu"
                title="Wipe cached wallet pairing + reload — use if signing prompts the wrong wallet"
              >Reset wallet</button>
              <div class="walletMenu__sep" aria-hidden="true"></div>
              <div class="walletMenu__row" :title="myAddress || ''">
                <span class="walletMenu__rowLabel">Address</span>
                <span class="walletMenu__rowValue walletMenu__rowValue--mono">{{ addressDisplay }}</span>
              </div>
              <div class="walletMenu__row">
                <span class="walletMenu__rowLabel">Access</span>
                <span :class="['walletMenu__rowValue', `walletMenu__access--${accessTierDisplay.split(' ')[0].toLowerCase()}`]">
                  {{ accessTierDisplay }}
                </span>
              </div>
              <div class="walletMenu__row">
                <span class="walletMenu__rowLabel">Wallet</span>
                <span class="walletMenu__rowValue">{{ walletBalanceDisplay }}</span>
              </div>
              <div class="walletMenu__row">
                <span class="walletMenu__rowLabel">Claimable</span>
                <span class="walletMenu__rowValue">{{ claimableSquaresDisplay }}</span>
              </div>
              <button
                type="button"
                class="walletMenu__item walletMenu__item--toggle"
                role="menuitemcheckbox"
                :aria-checked="balancesVisible ? 'true' : 'false'"
                @click="toggleBalancesVisible"
              >{{ balancesVisible ? 'Hide balances' : 'Show balances' }}</button>
            </div>
          </div>
          <div
            v-if="walletMenuOpen"
            class="walletMenu__backdrop"
            @click="closeWalletMenu"
            aria-hidden="true"
          ></div>
          <div class="actionButton topBanner__cashOut" @click="payNftHolderBC"> Cash Out TXL Earnings </div>
          <div :class="['txlRank', 'topBanner__unclaimed', txlPoolFlash ? 'txlRank--flash' : '']"> txl purse: {{ txlPoolValue.toFixed(3) }} tez </div>
          <div
            v-if="walletAddress.includes('error') || walletAddress.includes('failed')"
            class="actionButtonHelp walletReset topBanner__reset"
            @click="resetWallet"
            title="Wipe Beacon's cached state and start fresh — fixes stuck connect handshakes"
          >Reset wallet</div>
        </div>

        <div class="rowFlex" v-if="txlOwnsNft || txlCashStatus">
          <div class="txlRank" v-if="txlOwnsNft"> Your Claimable Share: {{ txlShare.toFixed(6) }} tez </div>
          <div class="txlRank" v-if="txlCashStatus"> {{ txlCashStatus }} </div>
        </div>

        <!-- Gated-build banners. Two variants:
             1. previewMode (TXL holder, not on invite list): the strongest
                signal — the holder can browse but can't transact. Banner
                styled amber so the user immediately knows actions are
                inert.
             2. The general dev-build banner for invited operators + URL-
                flagged beta visitors. Cream notice tone.
             Only renders when a gated tile is actually in view; default
             visitors with no allowlist/preview/URL never see either. -->
        <div v-if="previewMode && hasGatedTilesVisible" class="previewBanner" role="note">
          <span class="previewBanner__dot" aria-hidden="true"></span>
          TXL holder preview — wagering is disabled. Coming soon.
        </div>
        <div
          v-else-if="hasGatedTilesVisible"
          class="devBuildBanner"
          role="note"
        >
          <span class="devBuildBanner__dot" aria-hidden="true"></span>
          Private development build — apps below are in closed testing.
        </div>

        <div class="navCarousel" role="tablist" aria-label="Apps">
          <button
            type="button"
            class="navArrow navArrow--left"
            aria-label="Scroll apps left"
            @click="scrollNav(-1)"
          >‹</button>
          <div class="navStrip" ref="navStrip">
            <button
              v-for="app in navTiles"
              :key="app.id"
              type="button"
              role="tab"
              :aria-selected="activeView === app.id"
              :class="[
                'navPill',
                activeView === app.id ? 'navPill--active' : '',
                !app.mainnetReady ? 'navPill--soon' : '',
                app.tier === 'beta' ? 'navPill--beta' : '',
                app.tier === 'private' ? 'navPill--private' : '',
              ]"
              @click="selectGame(app.id)"
              :title="!app.mainnetReady ? `${app.name} isn't on mainnet yet — coming soon` : null"
            >
              {{ app.name }}
              <span
                v-if="!app.mainnetReady"
                class="navPill__badge"
                aria-label="coming soon"
              >SOON</span>
              <span
                v-else-if="app.tier === 'private'"
                class="navPill__badge navPill__badge--private"
                aria-label="private development tier"
              >PRIVATE</span>
              <span
                v-else-if="app.tier === 'beta'"
                class="navPill__badge navPill__badge--beta"
                aria-label="closed beta"
              >BETA</span>
            </button>
          </div>
          <button
            type="button"
            class="navArrow navArrow--right"
            aria-label="Scroll apps right"
            @click="scrollNav(1)"
          >›</button>
        </div>

        <!-- The wrapper div is load-bearing: <Transition mode="out-in">
             needs a single element root to track enter/leave. Some app
             components (e.g. tezTacToe) have multi-root templates, which
             leaves the transition unable to fire afterLeave — the next
             view never mounts and the pane goes blank. Wrapping the
             dynamic component guarantees one element root regardless. -->
        <transition name="view" mode="out-in">
          <div :key="activeView" class="viewSlot">
            <component
              :is="activeComponent"
              :socket="socket"
              :wallet="wallet"
              :tezos="tezos"
              :preview-mode="previewMode"
            />
          </div>
        </transition>
      </div>
    </div>
    <div class="label">
      Made with love by @jamin_b on telegram/discord and @jaminb12 on X.
    </div>
  </div>
</template>

<style>
/* ─── Global shared classes ──────────────────────────────────────────
   Crypto/web3 dark aesthetic. Tokens live in App.vue's :root. Every
   game component inherits these classes via mainBody — keep the names
   stable so we don't have to touch the children. */

.mainBody {
  margin: auto;
  padding: 14px 14px 20px;
  max-width: 720px;
  display: flex;
  flex-direction: column;
  border-radius: var(--ad-r-lg);
  background:
    radial-gradient(ellipse at 50% 0%, rgba(21, 41, 74, 0.10) 0%, transparent 60%),
    var(--ad-bg-base);
  border: 1px solid var(--ad-border-faint);
  box-shadow: var(--ad-shadow-card);
  color: var(--ad-text-1);
  font-family: var(--ad-font-body);
}
.centerBody {
  margin: auto;
  padding: 4px;
  max-width: 700px;
  display: flex;
  flex-direction: column;
  color: var(--ad-text-1);
  font-family: var(--ad-font-body);
  /* Flex/grid children default to min-width:auto = min-content. Without this,
     any long unbreakable token deep in the tree (an owner address, a select's
     widest option) can push this container past the viewport on phones. */
  min-width: 0;
  box-sizing: border-box;
}
.gameManagement {
  justify-content: center;
  width: 100%;
  flex: 1;
  min-width: 0;
  box-sizing: border-box;
  cursor: default;
}
/* Single-element root for <Transition mode="out-in"> — see template. */
.viewSlot {
  width: 100%;
}

.rowFlex {
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  gap: 6px;
}
.gameCenter {
  align-content: center;
  margin: auto;
  flex: 1;
}

/* ─── Surface chips (info / status / score) ──────────────────────── */
.gameInfo {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 12px;
  margin: 2px 0;
  border-radius: var(--ad-r-md);
  background: var(--ad-bg-elev-1);
  border: 1px solid var(--ad-border-soft);
  cursor: default;
  font-family: var(--ad-font-body);
  font-size: 13px;
  color: var(--ad-text-1);
  flex: 1;
  letter-spacing: 0.01em;
}
.gameSelect {
  margin: 2px 0;
  padding: 8px 12px;
  border-radius: var(--ad-r-md);
  background: var(--ad-bg-elev-1);
  border: 1px solid var(--ad-border-soft);
  color: var(--ad-text-1);
  font-family: var(--ad-font-body);
  font-size: 13px;
  flex: 1;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
}
.gameSelect:hover {
  background: var(--ad-bg-elev-2);
  border-color: var(--ad-border-mid);
}
.txlRank {
  align-content: center;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 12px;
  margin: 2px 0;
  border-radius: var(--ad-r-md);
  background: var(--ad-bg-elev-1);
  border: 1px solid var(--ad-border-faint);
  color: var(--ad-text-1);
  font-size: 12.5px;
  flex: 1;
  min-width: 0;
  overflow-wrap: anywhere;
  word-break: break-word;
  text-align: center;
  cursor: default;
  min-height: 38px;
}
/* Flash a chip green for ~1s when the value it shows grows. */
.txlRank--flash {
  animation: txlRankFlash 1s ease-out;
}
@keyframes txlRankFlash {
  0%, 15% {
    background: rgba(74, 222, 128, 0.30);
    border-color: rgba(74, 222, 128, 0.75);
    color: #4ade80;
  }
  100% {
    background: var(--ad-bg-elev-1);
    border-color: var(--ad-border-faint);
    color: var(--ad-text-1);
  }
}
.infoBox {
  justify-content: center;
  text-align: center;
  width: 95%;
  flex: 1;
  cursor: default;
  border-radius: var(--ad-r-md);
  border: 1px solid var(--ad-border-soft);
  background: var(--ad-bg-elev-1);
}

/* ─── Game canvas container ─────────────────────────────────────── */
.canvasContainer {
  border-radius: var(--ad-r-md);
  touch-action: manipulation;
  -webkit-user-select: none;
  user-select: none;
}
.canvasContainer canvas {
  display: block;
  max-width: 100%;
  height: auto;
}

/* ─── Tile/imageviewer used on welcome page (legacy fallback) ───── */
.imageViewer {
  flex: 1 1 200px;
  border: 1px solid var(--ad-border-soft);
  background: var(--ad-bg-elev-1);
  border-radius: var(--ad-r-md);
  padding: 8px;
  margin: 4px;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, transform 0.15s ease;
}
.imageViewer:hover {
  background: var(--ad-bg-elev-2);
  border-color: rgba(21, 41, 74, 0.45);
  transform: translateY(-1px);
}
.imageViewerBox {
  width: 100%;
  border-radius: var(--ad-r-sm);
  cursor: pointer;
}

/* ─── Info popup (rules drawer) ─────────────────────────────────── */
.infoPopup {
  position: relative;
  width: 100%;
  margin: 8px 0;
  padding: 14px 18px;
  background: linear-gradient(180deg, rgba(21, 41, 74, 0.10), rgba(21, 41, 74, 0.02));
  border: 1px solid var(--ad-border-soft);
  border-radius: var(--ad-r-md);
  text-align: left;
  cursor: not-allowed;
  color: var(--ad-text-2);
  font-size: 13px;
  line-height: 1.55;
}
.listItem {
  list-style-type: none;
  margin: 4px 0;
}

/* ─── Buttons: the workhorse ─────────────────────────────────────
   .actionButton           — default secondary button
   .actionButtonSelected   — currently-active state in nav
   .actionButtonHelp       — tertiary / muted help button
   All three share the same shape; the variants differ only in fill. */
.actionButton,
.actionButtonSelected,
.actionButtonHelp {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
  margin: 3px 0;
  min-height: 40px;
  border-radius: var(--ad-r-pill);
  border: 1px solid var(--ad-border-soft);
  background: var(--ad-bg-elev-1);
  color: var(--ad-text-1);
  flex: 1;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.02em;
  font-family: var(--ad-font-body);
  cursor: pointer;
  text-align: center;
  transition: background 0.18s ease, border-color 0.18s ease,
              transform 0.12s ease, box-shadow 0.2s ease;
  /* Subtle inner highlight so the button feels lit from above. */
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04);
}
.actionButton:hover,
.actionButtonHelp:hover {
  background: var(--ad-bg-elev-2);
  border-color: rgba(21, 41, 74, 0.55);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.06),
    var(--ad-glow-violet);
}
.actionButton:active,
.actionButtonHelp:active,
.actionButtonSelected:active {
  transform: translateY(1px) scale(0.99);
}
.actionButtonHelp {
  color: var(--ad-text-2);
  cursor: help;
}

/* The "selected" state in the top nav — gold gradient + glow. */
.actionButtonSelected {
  background: var(--ad-grad-gold);
  color: #ffffff;
  border-color: rgba(200, 16, 46, 0.7);
  font-weight: 700;
  text-shadow: 0 1px 0 rgba(255, 255, 255, 0.25);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.3),
    var(--ad-glow-gold);
}
.actionButtonSelected:hover {
  filter: brightness(1.05);
}

/* ─── App-nav carousel (TXL Manager first, then games) ─────────
   A horizontally-scrolling pill row flanked by arrow buttons. The
   strip itself is a CSS scroll-snap container so the active pill
   keeps near a snap-point as the user clicks the arrows. The pills
   carry their own active/inactive styling so we don't need the
   .actionButton family rules to apply (they would force flex:1 and
   wrap-stretch the pills full width). */
.navCarousel {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 6px 0 8px;
  width: 100%;
}
.navStrip {
  flex: 1;
  display: flex;
  gap: 6px;
  overflow-x: auto;
  scroll-snap-type: x proximity;
  scroll-behavior: smooth;
  /* Hide scrollbar without removing the wheel/swipe behavior. */
  scrollbar-width: none;
  -ms-overflow-style: none;
  padding: 2px 4px;
}
.navStrip::-webkit-scrollbar { display: none; }
.navPill {
  flex: 0 0 auto;
  scroll-snap-align: center;
  padding: 8px 14px;
  min-height: 36px;
  border-radius: var(--ad-r-pill);
  border: 1px solid var(--ad-border-soft);
  background: var(--ad-bg-elev-1);
  color: var(--ad-text-1);
  font-family: var(--ad-font-body);
  font-size: 12.5px;
  font-weight: 600;
  letter-spacing: 0.02em;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease,
              transform 0.1s ease;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04);
}
.navPill:hover {
  background: var(--ad-bg-elev-2);
  border-color: rgba(21, 41, 74, 0.55);
}
.navPill:active { transform: scale(0.97); }

/* "SOON" badge inside the pill when on mainnet and the app's mainnet KT1
   is still a placeholder. The pill itself stays clickable so users can
   read about the game; the component's own placeholder guard handles
   the empty-state inside the view. */
.navPill--soon {
  opacity: 0.62;
}
.navPill--soon:hover {
  opacity: 0.9;
}
.navPill__badge {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 999px;
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.08em;
  background: rgba(21, 41, 74, 0.18);
  color: rgba(200, 16, 46, 0.92);
  border: 1px solid rgba(21, 41, 74, 0.35);
  vertical-align: middle;
  line-height: 1.4;
}
.navPill--active .navPill__badge {
  background: rgba(26, 16, 4, 0.18);
  color: #ffffff;
  border-color: rgba(26, 16, 4, 0.35);
}
/* Tier badges — neutral cyan for BETA, warm amber for PRIVATE. Distinct
   palette from the purple SOON badge so a tile carrying both signals
   (e.g. a private app whose KT1 isn't yet originated — shouldn't happen
   today but might) doesn't visually collide. */
.navPill__badge--beta {
  background: rgba(96, 165, 250, 0.18);
  color: #93c5fd;
  border-color: rgba(96, 165, 250, 0.45);
}
.navPill__badge--private {
  background: rgba(245, 158, 11, 0.18);
  color: #fbbf24;
  border-color: rgba(245, 158, 11, 0.5);
}
.navPill--active .navPill__badge--beta,
.navPill--active .navPill__badge--private {
  background: rgba(26, 16, 4, 0.18);
  color: #ffffff;
  border-color: rgba(26, 16, 4, 0.35);
}

/* "Private development build" banner — sits between the wallet/cash-out
   row and the nav carousel. Subtle muted strip so it reads as a notice,
   not a warning. Only renders when a gated tile is actually visible
   (default visitors with no ?beta=1 + no allowlisted wallet never see it). */
.devBuildBanner {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 14px;
  margin: 8px 0;
  border-radius: var(--ad-r-md);
  background: rgba(21, 41, 74, 0.08);
  border: 1px solid rgba(21, 41, 74, 0.28);
  color: rgba(200, 16, 46, 0.95);
  font-family: var(--ad-font-mono);
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
.devBuildBanner__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgba(200, 16, 46, 0.85);
  box-shadow: 0 0 8px rgba(200, 16, 46, 0.5);
  flex: 0 0 8px;
}

/* TXL-holder preview banner — louder than the operator dev banner so a
   holder doesn't try to wager and wonder why nothing happens. Amber
   border + slightly heavier weight; not a warning red, since this is
   the intended UX for the holder, not an error. */
.previewBanner {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 14px;
  margin: 8px 0;
  border-radius: var(--ad-r-md);
  background: rgba(251, 191, 36, 0.12);
  border: 1px solid rgba(251, 191, 36, 0.55);
  color: #fbbf24;
  font-family: var(--ad-font-mono);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.previewBanner__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #fbbf24;
  box-shadow: 0 0 8px rgba(251, 191, 36, 0.7);
  flex: 0 0 8px;
}

.navPill--active {
  background: var(--ad-grad-gold);
  color: #ffffff;
  border-color: rgba(200, 16, 46, 0.7);
  font-weight: 700;
  text-shadow: 0 1px 0 rgba(255, 255, 255, 0.25);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.3),
    var(--ad-glow-gold);
}
.navArrow {
  flex: 0 0 auto;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 1px solid var(--ad-border-soft);
  background: var(--ad-bg-elev-1);
  color: var(--ad-text-1);
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  user-select: none;
  transition: background 0.15s ease, border-color 0.15s ease,
              transform 0.1s ease;
}
.navArrow:hover {
  background: var(--ad-bg-elev-2);
  border-color: rgba(21, 41, 74, 0.55);
}
.navArrow:active { transform: scale(0.92); }

/* ─── Native select — styled to match the buttons ─────────────── */
.selectBox {
  padding: 8px 32px 8px 12px;
  margin: 2px 0;
  border-radius: var(--ad-r-md);
  border: 1px solid var(--ad-border-soft);
  background-color: var(--ad-bg-elev-1);
  background-image:
    linear-gradient(45deg, transparent 50%, var(--ad-text-2) 50%),
    linear-gradient(135deg, var(--ad-text-2) 50%, transparent 50%);
  background-position:
    calc(100% - 16px) 50%,
    calc(100% - 10px) 50%;
  background-size: 6px 6px, 6px 6px;
  background-repeat: no-repeat;
  color: var(--ad-text-1);
  font-family: var(--ad-font-body);
  font-size: 13px;
  flex: 1;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  transition: background-color 0.15s ease, border-color 0.15s ease;
  min-height: 38px;
}
.selectBox:hover {
  background-color: var(--ad-bg-elev-2);
  border-color: var(--ad-border-mid);
}
.selectBox:focus {
  outline: none;
  border-color: rgba(21, 41, 74, 0.65);
  box-shadow: 0 0 0 3px rgba(21, 41, 74, 0.18);
}
.selectBox option {
  background-color: var(--ad-bg-base);
  color: var(--ad-text-1);
}

/* The "Made with love" footer-style label scattered in components. */
.label {
  text-align: center;
  color: var(--ad-text-3);
  font-size: 11px;
  letter-spacing: 0.04em;
  margin: 12px auto 0;
  font-family: var(--ad-font-body);
}

/* Wallet pills: there are two — a desktop one with full status text
   (direct toggle on click) and a mobile-only hamburger that opens a
   dropdown menu. Each is hidden at the other breakpoint. Defined here
   (before the @media block) so source order doesn't accidentally
   re-show them. */
.topBanner__wallet--mobile { display: none; }
.topBanner__walletWrap {
  position: relative;
  display: none;
}

/* Hamburger glyph — three crisp lines, color from the parent button.
   When the menu is open the top/bottom lines fold into an X. */
.hamburger {
  display: inline-flex;
  flex-direction: column;
  justify-content: space-between;
  width: 22px;
  height: 16px;
  pointer-events: none;
}
.hamburger > span {
  display: block;
  height: 2px;
  width: 100%;
  background: currentColor;
  border-radius: 1px;
  transition: transform 0.18s ease, opacity 0.18s ease;
  transform-origin: center;
}
.hamburger--open > span:nth-child(1) { transform: translateY(7px) rotate(45deg); }
.hamburger--open > span:nth-child(2) { opacity: 0; }
.hamburger--open > span:nth-child(3) { transform: translateY(-7px) rotate(-45deg); }

/* Dropdown anchored under the hamburger. Hidden on desktop because the
   wrapper is hidden; mobile @media flips both to visible. */
.walletMenu {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 50;
  min-width: 160px;
  padding: 6px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  background: var(--ad-bg-base);
  border: 1px solid var(--ad-border-mid);
  border-radius: var(--ad-r-md);
  box-shadow: var(--ad-shadow-card);
}
.walletMenu__item {
  appearance: none;
  background: transparent;
  border: none;
  color: var(--ad-text-1);
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-align: left;
  padding: 10px 12px;
  border-radius: var(--ad-r-sm);
  cursor: pointer;
  transition: background 0.12s ease;
}
.walletMenu__item:hover,
.walletMenu__item:focus-visible {
  background: var(--ad-bg-elev-2);
  outline: none;
}
.walletMenu__item--toggle {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--ad-gold-2);
}
.walletMenu__item--reset {
  font-size: 12px;
  letter-spacing: 0.04em;
  color: var(--ad-red-1);
  opacity: 0.85;
}
.walletMenu__item--reset:hover,
.walletMenu__item--reset:focus-visible {
  background: rgba(196, 82, 79, 0.12);
  opacity: 1;
}
.walletMenu__sep {
  height: 1px;
  margin: 4px 6px;
  background: var(--ad-border-mid);
}
.walletMenu__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 12px;
  font-family: inherit;
  font-size: 13px;
}
.walletMenu__rowLabel {
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--ad-text-3);
  font-size: 11px;
}
.walletMenu__rowValue {
  font-variant-numeric: tabular-nums;
  color: var(--ad-text-1);
  font-weight: 600;
}
.walletMenu__rowValue--mono {
  font-family: var(--ad-font-mono);
  font-size: 11px;
  letter-spacing: 0.02em;
}
/* Access-tier color coding. Operator = green (full unlock). TXL preview
   = amber (read-only). Public + Not = muted gray so the user sees at a
   glance they're outside the gated set. */
.walletMenu__access--operator { color: #4ade80; }
.walletMenu__access--txl      { color: #fbbf24; }
.walletMenu__access--public,
.walletMenu__access--not      { color: rgba(255, 255, 255, 0.45); }
.walletMenu__backdrop {
  position: fixed;
  inset: 0;
  z-index: 40;
  background: transparent;
}

/* ─── Site-wide mobile rules ─────────────────────────────────────────
   Two-tier responsive pass: ≤480px (phone portrait) and 481–768px
   (phone landscape / large phone). The shell is non-scoped, so
   anything we tighten here also affects every game component via the
   shared .actionButton / .gameInfo / .rowFlex classes — that's how
   we get mobile coverage without editing every child component. */

/* Phone portrait — most aggressive */
@media (max-width: 480px) {
  body { padding: 8px 0; }
  .mainBody {
    padding: 10px 8px 16px;
    border-radius: var(--ad-r-md);
    min-width: 0;
    overflow-x: hidden;
  }
  .centerBody {
    overflow-x: hidden;
    max-width: 100%;
  }
  /* Lift every interactive surface to a ≥44px Apple-HIG touch target. */
  .actionButton,
  .actionButtonSelected,
  .actionButtonHelp {
    min-height: 44px;
    padding: 10px 14px;
    font-size: 13.5px;
  }
  .selectBox { min-height: 44px; }
  .gameSelect { min-height: 44px; padding: 10px 12px; }
  .gameInfo { font-size: 12.5px; padding: 8px 10px; }
  .txlRank { min-height: 42px; font-size: 12px; padding: 8px 10px; }
  /* Nav carousel: lift pill + arrow tap targets, shrink type slightly */
  .navPill { min-height: 44px; padding: 10px 14px; font-size: 12px; }
  .navArrow { width: 36px; height: 36px; font-size: 20px; }
  /* When rows wrap, give items more breathing room */
  .rowFlex { gap: 8px; }
  .label { font-size: 10px; margin-top: 10px; }

  /* ─── Top banner: stacked layout instead of one crushed row ────
     Row 1: hamburger pill (left) + Cash Out TXL Earnings (hidden — moved
            into the hamburger menu)
     Row 2: txl purse  (full-bleed)
     Row 3 (only if error): Reset wallet full-width
     Buttons revert to flex:0 so flex-basis controls the split. */
  .topBanner {
    display: flex;
    flex-wrap: wrap;
    row-gap: 8px;
    column-gap: 8px;
  }
  .topBanner > .actionButton,
  .topBanner > .actionButtonHelp,
  .topBanner > .txlRank {
    flex: 0 0 auto;
    margin: 0;
  }
  /* Swap wallet pills: hide the desktop text pill, show the hamburger
     wrapper. The Cash Out TXL Earnings pill is hidden because the
     hamburger menu now exposes that action — keeps the row tight. */
  .topBanner__wallet--desktop { display: none; }
  .topBanner__walletWrap { display: block; flex: 0 0 44px; }
  .topBanner__wallet--mobile {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 44px;
    width: 44px;
    min-width: 44px;
    height: 44px;
    min-height: 44px;
    padding: 0;
  }
  .topBanner__cashOut { display: none; }
  .topBanner__unclaimed {
    flex: 1 1 100%;
  }
  .topBanner__reset {
    flex: 1 1 100%;
  }
}

/* Phone landscape / large phone — gentler */
@media (min-width: 481px) and (max-width: 768px) {
  .mainBody {
    padding: 12px 12px 18px;
    max-width: 100%;
  }
  .actionButton,
  .actionButtonSelected,
  .actionButtonHelp {
    min-height: 42px;
  }
  .navPill { min-height: 40px; }
  .navArrow { width: 34px; height: 34px; }
}
</style>
